import React, { useState, useEffect, useCallback, useContext } from "react";

import AccessDeniedResponse from "../common/AccessDeniedResponse";
import {
  EmptyState,
  FilterBar,
  LoadingState,
} from "../common/SurfaceUI";

import TimetableList from "./TimetableList";
import TimetableActions from './TimetableActions';

import { YearContext } from "../../contexts/yearContext";
import { AuthContext } from "../../contexts/authContext";

import lbl from "../../data/Labels.json";
import db from "../../services/query";
import "./timetable.css";

/**
 * Testaria timetable_home parity:
 * entry-year weeks + class/faculty/teacher filters + week data from allWeek (MIS mode).
 * Teachers may also view personal week when no class is selected.
 */
const Timetable = () => {
  const { entryYear } = useContext(YearContext);
  const { accountProfile } = useContext(AuthContext);

  const positionId = Number(accountProfile?.position_id || 0);
  const isTeacher = positionId === 1;
  const scopedFacultyId =
    accountProfile?.faculty_id != null && Number(accountProfile.faculty_id) > 0
      ? Number(accountProfile.faculty_id)
      : null;
  const showFacultyFilter = scopedFacultyId == null;

  const [weeks, setWeeks] = useState([]);
  const [weekStart, setWeekStart] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [needsClass, setNeedsClass] = useState(true);
  const [searched, setSearched] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const [faculties, setFaculties] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [facultyId, setFacultyId] = useState(scopedFacultyId ? String(scopedFacultyId) : "");
  const [departmentId, setDepartmentId] = useState("");
  const [classId, setClassId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [teacherId, setTeacherId] = useState(isTeacher ? "self" : "");

  const effectiveFacultyId = scopedFacultyId || (facultyId ? Number(facultyId) : null);
  const groupVisible = Number(effectiveFacultyId) === 1;

  const loadWeekData = useCallback(
    async (startDate, filters = {}) => {
      if (!startDate) return;
      const {
        classId: cls = classId,
        teacherId: tch = teacherId,
        groupId: grp = groupId,
        facultyId: fac = effectiveFacultyId,
        entryYearId = entryYear,
        personal = false,
      } = filters;

      const wantsMis =
        !personal && (Boolean(cls) || (Boolean(tch) && tch !== "self"));
      if (!isTeacher && !cls && !(tch && tch !== "self")) {
        setNeedsClass(true);
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setDenied(false);
      setErrorMsg("");
      setNeedsClass(false);
      try {
        const params = new URLSearchParams({ startDate: String(startDate) });
        if (entryYearId) params.set("entryYearId", String(entryYearId));
        if (wantsMis) {
          params.set("mis", "1");
          if (cls) params.set("classId", String(cls));
          if (grp) params.set("groupId", String(grp));
          if (fac) params.set("facultyId", String(fac));
          if (tch && tch !== "self") params.set("teacherId", String(tch));
        }
        const { data: body } = await db.getAll(`timetables/allWeek?${params.toString()}`);
        const list = Array.isArray(body?.data) ? body.data : [];
        setRows(list);
      } catch (err) {
        const status = err?.response?.status;
        const body = err?.response?.data;
        if (status === 403 || body?.error?.statusCode === 403) {
          setDenied(true);
          setRows([]);
        } else {
          setRows([]);
          setErrorMsg(body?.message || body?.error?.message || "بارگیری تقسیم اوقات ناموفق بود.");
        }
      } finally {
        setLoading(false);
      }
    },
    [classId, teacherId, groupId, effectiveFacultyId, entryYear, isTeacher]
  );

  // Entry-year change: clear class/week so Sem1 vs other week lists cannot leak across semesters.
  useEffect(() => {
    setWeekStart("");
    setRows([]);
    setSearched(false);
    setNeedsClass(true);
    setClassId("");
    setGroupId("");
  }, [entryYear, isTeacher]);

  // Weeks depend on entry year + selected class (EY17 Sem1 gets +2 makeup weeks and later exams).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (entryYear) params.set("entryYearId", String(entryYear));
        if (classId) params.set("classId", String(classId));
        const q = params.toString();
        const route = q ? `dropdowns/weeks?${q}` : "dropdowns/weeks";
        const { data: body } = await db.getAll(route);
        if (cancelled) return;
        const list = Array.isArray(body?.data) ? body.data : [];
        setWeeks(list);
        setWeekStart((prev) => {
          if (prev && list.some((w) => String(w.week_start || w.id) === String(prev))) {
            return prev;
          }
          const current =
            list.find((w) => Number(w.is_current_week) === 1) || list[0];
          return current?.week_start || current?.id || "";
        });
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const body = err?.response?.data;
        if (status === 403 || body?.error?.statusCode === 403) {
          setDenied(true);
        } else {
          setErrorMsg(body?.message || "بارگیری هفته‌ها ناموفق بود.");
        }
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entryYear, classId, isTeacher]);

  useEffect(() => {
    if (!showFacultyFilter) return;
    let cancelled = false;
    db.getAll("dropdowns/faculties")
      .then(({ data: body }) => {
        if (!cancelled) setFaculties(body?.data || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [showFacultyFilter]);

  useEffect(() => {
    if (!entryYear) {
      setClasses([]);
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams({ entryYearId: String(entryYear) });
    if (effectiveFacultyId) params.set("facultyId", String(effectiveFacultyId));
    db.getAll(`dropdowns/classes?${params.toString()}`)
      .then(({ data: body }) => {
        if (cancelled) return;
        const list = body?.data || [];
        setClasses(
          departmentId
            ? list.filter((c) => String(c.department) === String(departmentId))
            : list
        );
      })
      .catch(() => {
        if (!cancelled) setClasses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [entryYear, effectiveFacultyId, departmentId]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    if (effectiveFacultyId) params.set("facultyId", String(effectiveFacultyId));
    const q = params.toString();
    db.getAll(`dropdowns/timetableTeachers${q ? `?${q}` : ""}`)
      .then(({ data: body }) => {
        if (!cancelled) setTeachers(body?.data || []);
      })
      .catch(() => {
        if (!cancelled) setTeachers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveFacultyId]);

  useEffect(() => {
    let cancelled = false;
    db.getAll("dropdowns/departments")
      .then(({ data: body }) => {
        if (cancelled) return;
        const all = body?.data || [];
        setDepartments(
          effectiveFacultyId
            ? all.filter((d) => Number(d.faculty) === Number(effectiveFacultyId))
            : all
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [effectiveFacultyId]);

  useEffect(() => {
    if (!classId || !groupVisible) {
      setGroups([]);
      setGroupId("");
      return;
    }
    let cancelled = false;
    db.getAll(`dropdowns/groups?classId=${encodeURIComponent(classId)}`)
      .then(({ data: body }) => {
        if (!cancelled) setGroups(body?.data || []);
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [classId, groupVisible]);

  const onSearch = () => {
    if (!weekStart) return;
    if (!isTeacher && !classId && !teacherId) {
      setNeedsClass(true);
      setRows([]);
      return;
    }
    setSearched(true);
    setFilterOpen(false);
    loadWeekData(weekStart);
  };

  const onWeekChange = (e) => {
    const next = e.target.value;
    setWeekStart(next);
    if (!searched) return;
    if (isTeacher && !classId) {
      loadWeekData(next, { personal: true });
    } else if (classId || teacherId) {
      loadWeekData(next);
    }
  };

  const tableData = { data: rows };
  const selectedWeek = weeks.find(
    (week) => String(week.week_start || week.id) === String(weekStart)
  );
  const selectedClass = classes.find((item) => String(item.id) === String(classId));
  const selectedTeacher = teachers.find((item) => String(item.id) === String(teacherId));
  const filterSummary = [
    selectedClass?.name,
    selectedWeek?.date_title || selectedWeek?.name,
    isTeacher ? accountProfile?.full_name || accountProfile?.name : selectedTeacher?.name,
  ].filter(Boolean).join(" · ") || "انتخاب";

  return (
    <div className="main-content timetable-page mis-surface">
      <div className="main-content-header">
        <div className="right-content" />
        <div className="center-content">
        {!denied ? (
        <details
          className="timetable-filter-disclosure"
          open={filterOpen}
          onToggle={(event) => setFilterOpen(event.currentTarget.open)}
        >
          <summary aria-label="باز کردن فیلتر تقسیم اوقات">
            <span>{filterSummary}</span>
          </summary>
        <FilterBar
          className="timetable-filters"
          title="تصفیه"
          disabled={loading}
        >
          {showFacultyFilter ? (
            <label>
              {lbl.Faculties}
              <select
                aria-label="Faculty filter"
                value={facultyId}
                onChange={(e) => {
                  setFacultyId(e.target.value);
                  setClassId("");
                  setDepartmentId("");
                  setGroupId("");
                }}
              >
                <option value="">همه پوهنحی‌ها</option>
                {faculties.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {departments.length > 0 ? (
            <label>
              {lbl.Department}
              <select
                aria-label="Department filter"
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
              >
                <option value="">همه</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <label>
            {lbl.Classes}
            <select
              aria-label="Class filter"
              value={classId}
              onChange={(e) => {
                setClassId(e.target.value);
                setGroupId("");
              }}
            >
              <option value="">انتخاب صنف</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          {groupVisible ? (
            <label>
              گروپ
              <select
                aria-label="Group filter"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
              >
                <option value="">همه</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <label>
            {lbl.SelectWeek}
            <select
              name="timetable-week"
              value={weekStart}
              onChange={onWeekChange}
              aria-label={lbl.SelectWeek}
            >
              {!weeks.length ? <option value="">هفته‌ای موجود نیست</option> : null}
              {weeks.map((w) => (
                <option key={w.week_start || w.id} value={w.week_start || w.id}>
                  {w.date_title || w.name}
                  {Number(w.is_current_week) === 1 ? " · هفته جاری" : ""}
                </option>
              ))}
            </select>
          </label>

          <label>
            {lbl.Teacher}
            <select
              aria-label="Teacher filter"
              value={isTeacher ? "self" : teacherId}
              disabled={isTeacher}
              onChange={(e) => setTeacherId(e.target.value)}
            >
              {isTeacher ? (
                <option value="self">
                  {accountProfile?.full_name || accountProfile?.name || "استاد جاری"}
                </option>
              ) : (
                <>
                  <option value="">همه / اختیاری</option>
                  {teachers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </label>

          <button
            className="mis-button timetable-search-button"
            type="button"
            onClick={onSearch}
            data-testid="timetable-search"
          >
            {lbl.Search}
          </button>
        </FilterBar>
        </details>
      ) : null}
        </div>
        <div className="left-content">
          {!denied ? <TimetableActions classId={classId} classes={classes} weekStart={weekStart} onSaved={()=>loadWeekData(weekStart)}/> : null}
        </div>
      </div>

      <div className="main-content-body pagination timetable-content">
        {denied ? (
          <AccessDeniedResponse />
        ) : loading ? (
          <LoadingState label={lbl.Loading} />
        ) : errorMsg ? (
          <EmptyState
            tone="error"
            role="alert"
            title="بارگیری برنامه ناموفق بود"
            description={errorMsg}
          />
        ) : needsClass ? (
          <EmptyState
            title="لطفا صنف را انتخاب نمایید."
            description="می‌توانید برنامه را بر اساس صنف یا استاد فیلتر و سپس جستجو کنید."
          />
        ) : (
          <TimetableList data={tableData} weekStart={weekStart} classes={classes} onSaved={()=>loadWeekData(weekStart)} />
        )}
      </div>
      <div className="main-content-footer">
        <div className="route-history-area" />
        <div className="pagination-area" />
      </div>
    </div>
  );
};

export default Timetable;
